import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdSearch, MdExpandMore, MdExpandLess, MdFilterList } from 'react-icons/md';
import type { MenuItem } from '../menuItems/types';

interface MenuSelectorProps {
  items: MenuItem[];
  selectedItems: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  loading?: boolean;
}

const MenuSelector: React.FC<MenuSelectorProps> = ({ 
  items, 
  selectedItems, 
  onSelectionChange,
  loading 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    items.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, [items]);

  // Filter items based on search and category
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           item.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategories.length === 0 || 
                             selectedCategories.includes(item.category);
      return matchesSearch && matchesCategory && item.is_enabled;
    });
  }, [items, searchQuery, selectedCategories]);

  const toggleItem = (itemId: string) => {
    if (selectedItems.includes(itemId)) {
      onSelectionChange(selectedItems.filter(id => id !== itemId));
    } else {
      onSelectionChange([...selectedItems, itemId]);
    }
  };

  const toggleCategory = (category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const getSelectedItemsText = () => {
    if (selectedItems.length === 0) return 'Select menu items...';
    if (selectedItems.length === 1) {
      const item = items.find(i => i.pk_id === selectedItems[0]);
      return item?.name || 'Select menu items...';
    }
    return `${selectedItems.length} items selected`;
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Main Input */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-[#212121] border border-gray-700 rounded-xl text-left flex items-center justify-between hover:border-gray-600 transition-colors"
      >
        <span className={`${selectedItems.length > 0 ? 'text-white' : 'text-gray-400'}`}>
          {getSelectedItemsText()}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <MdExpandMore className="text-white text-xl" />
        </motion.div>
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-[#212121] border border-gray-700 rounded-xl overflow-hidden z-20 max-h-96 flex flex-col"
          >
            {/* Search and Filter Bar */}
            <div className="p-3 border-b border-gray-700 space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search menu items..."
                    className="w-full pl-10 pr-4 py-2 bg-[#2a2a2a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#EE4423] transition-colors"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2 rounded-lg transition-colors ${
                    showFilters ? 'bg-[#EE4423] text-white' : 'bg-[#2a2a2a] text-gray-400 hover:text-white'
                  }`}
                >
                  <MdFilterList className="text-xl" />
                </button>
              </div>

              {/* Category Filters */}
              <AnimatePresence>
                {showFilters && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-2">
                      <p className="text-white text-xs font-medium mb-2">Filter by Category</p>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((category) => (
                          <label
                            key={category}
                            className="flex items-center gap-2 px-3 py-1 bg-[#2a2a2a] rounded-lg cursor-pointer hover:bg-gray-700 transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(category)}
                              onChange={() => toggleCategory(category)}
                              className="w-4 h-4 rounded border-gray-600 text-[#EE4423] focus:ring-[#EE4423]"
                            />
                            <span className="text-white text-xs">{category}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Items List */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-4 text-center text-gray-400">Loading menu items...</div>
              ) : filteredItems.length === 0 ? (
                <div className="p-4 text-center text-gray-400">No items found</div>
              ) : (
                filteredItems.map((item) => (
                  <label
                    key={item.pk_id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 cursor-pointer transition-colors border-b border-gray-800 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.pk_id)}
                      onChange={() => toggleItem(item.pk_id)}
                      className="w-5 h-5 rounded border-gray-600 text-[#EE4423] focus:ring-[#EE4423]"
                    />
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{item.name}</p>
                      {/* <span className="text-xs text-gray-400">{item.category}</span> */}
                    </div>
                  </label>
                ))
              )}
            </div>

            {/* Selected Count Footer */}
            {selectedItems.length > 0 && (
              <div className="px-4 py-2 bg-[#2a2a2a] border-t border-gray-700 flex items-center justify-between">
                <span className="text-white text-sm">{selectedItems.length} item(s) selected</span>
                <button
                  type="button"
                  onClick={() => {
                    onSelectionChange([]);
                    setIsOpen(false);
                  }}
                  className="text-xs text-gray-400 hover:text-white transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MenuSelector;
